<template>
  <div class="user-profile">
    <div class="profile-content" v-loading="loading">
      <!-- 整体卡片容器 -->
      <el-card class="main-card">
        <!-- 基本信息区域 -->
        <div class="section basic-info-section">
          <div class="section-header">
            <h3 class="section-title">基本信息</h3>
          </div>
          <div class="basic-info-content">
            <!-- 左侧头像区域 -->
            <div class="avatar-area" @click="handleAvatarClick">
              <el-avatar
                  :size="88"
                  :src="getAvatarUrl()"
                  class="user-avatar"
              />
              <div class="avatar-hint">
                <span class="hint-text">点击更换头像</span>
              </div>
            </div>

            <!-- 右侧信息区域 -->
            <div class="info-area">
              <div class="info-grid">
                <!-- 第一行：账号、昵称 -->
                <div class="info-row">
                  <div class="info-item">
                    <label class="info-label">账号</label>
                    <span class="info-value">{{ profile.username || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">昵称</label>
                    <div class="info-with-action">
                      <span class="info-value">{{ profile.nickname || '-' }}</span>
                      <el-button
                          type="primary"
                          link
                          size="small"
                          @click="openNicknameDialog"
                          class="edit-btn"
                      >
                        <el-icon><EditPen /></el-icon>
                      </el-button>
                    </div>
                  </div>
                </div>

                <!-- 第二行：账号类型 -->
                <div class="info-row">
                  <div class="info-item">
                    <label class="info-label">账号类型</label>
                    <span class="info-value">{{ profile.userType || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <!-- 空占位，保持网格对齐 -->
                  </div>
                </div>

                <!-- 第三行：账号等级、联系手机 -->
                <div class="info-row">
                  <div class="info-item">
                    <label class="info-label">账号等级</label>
                    <span class="info-value">
                      <el-tag
                          v-if="profile.userLevel"
                          type="primary"
                          size="small"
                          class="user-level-tag"
                      >
                        {{ profile.userLevel }}
                      </el-tag>
                      <span v-else>-</span>
                    </span>
                  </div>
                  <div class="info-item">
                    <label class="info-label">联系手机</label>
                    <div class="info-with-action">
                      <span class="info-value">{{ profile.phone || '-' }}</span>
                      <el-button
                          type="default"
                          size="small"
                          class="pwd-modify-btn"
                          @click="handleAction('modify')"
                      >
                        修改手机
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分隔线 -->
        <div class="section-divider"></div>

        <!-- 登录方式区域 -->
        <div class="section login-methods-section">
          <div class="section-header">
            <div class="section-title-with-subtitle">
              <h3 class="section-title">登录方式</h3>
              <span class="section-subtitle">账号支持多种登录方式，便捷管理账号</span>
            </div>
          </div>
          <div class="methods-grid-container">
            <!-- 登录方式网格布局 -->
            <div class="methods-grid">
              <!-- 第一行第一列：账号密码 -->
              <div class="method-grid-item" v-if="profile.loginMethods[0]">
                <div class="method-content-single-line">
                  <div class="method-info-single-line">
                    <span class="method-type">{{ profile.loginMethods[0].type }}</span>
                    <span class="method-value">{{ profile.loginMethods[0].value }}</span>
                  </div>
                  <div class="method-actions">
                    <span
                        class="method-status"
                        :class="{
                          'status-bound': profile.loginMethods[0].isBound,
                          'status-unbound': !profile.loginMethods[0].isBound
                        }"
                    >
                      {{ profile.loginMethods[0].status }}
                    </span>
                    <el-button
                        v-if="profile.loginMethods[0].type === '账号密码' && !profile.loginMethods[0].isBound"
                        type="primary"
                        link
                        size="small"
                        @click="handleAction('set', profile.loginMethods[0])"
                    >
                      设置
                    </el-button>
                    <!-- 修改密码按钮 -->
                    <el-button
                        v-else-if="profile.loginMethods[0].type === '账号密码' && profile.loginMethods[0].isBound"
                        type="default"
                        size="small"
                        class="pwd-modify-btn"
                        @click="handleAction('changePassword', profile.loginMethods[0])"
                    >
                      修改密码
                    </el-button>
                  </div>
                </div>
              </div>

              <!-- 第一行第二列：微信 -->
              <div class="method-grid-item" v-if="profile.loginMethods[1]">
                <div class="method-content-single-line">
                  <div class="method-info-single-line">
                    <span class="method-type">{{ profile.loginMethods[1].type }}</span>
                    <span class="method-value">{{ profile.loginMethods[1].value }}</span>
                  </div>
                  <div class="method-actions">
                    <span
                        class="method-status"
                        :class="{
                          'status-bound': profile.loginMethods[1].isBound,
                          'status-unbound': !profile.loginMethods[1].isBound
                        }"
                    >
                      {{ profile.loginMethods[1].status }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 第二行第一列：登录手机 -->
              <div class="method-grid-item full-width-item" v-if="profile.loginMethods[2]">
                <div class="method-content-single-line">
                  <div class="method-info-single-line">
                    <span class="method-type">{{ profile.loginMethods[2].type }}</span>
                    <span class="method-value">{{ profile.loginMethods[2].value }}</span>
                  </div>
                  <div class="method-actions">
                    <span
                        class="method-status"
                        :class="{
                          'status-bound': profile.loginMethods[2].isBound,
                          'status-unbound': !profile.loginMethods[2].isBound
                        }"
                    >
                      {{ profile.loginMethods[2].status }}
                    </span>
                    <!--                    <el-button-->
                    <!--                        v-if="profile.loginMethods[2].type === '登录手机' && profile.loginMethods[2].isBound"-->
                    <!--                        type="primary"-->
                    <!--                        link-->
                    <!--                        size="small"-->
                    <!--                        @click="handleAction('modify', profile.loginMethods[2])"-->
                    <!--                    >-->
                    <!--                      修改-->
                    <!--                    </el-button>-->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部安全提示区域 -->
        <div class="section description-section">
          <div class="security-tips">
            <div class="security-tip-item">
              <div class="tip-icon tip-icon--blue">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <div class="tip-text">
                <div class="tip-title">定期修改密码</div>
                <div class="tip-desc">建议每 90 天更新一次，保障账号安全</div>
              </div>
            </div>
            <div class="security-tip-divider"></div>
            <div class="security-tip-item">
              <div class="tip-icon tip-icon--orange">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </div>
              <div class="tip-text">
                <div class="tip-title">绑定多种登录</div>
                <div class="tip-desc">微信 + 手机 + 密码，避免单一登录失效</div>
              </div>
            </div>
            <div class="security-tip-divider"></div>
            <div class="security-tip-item">
              <div class="tip-icon tip-icon--green">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
              </div>
              <div class="tip-text">
                <div class="tip-title">遇到问题</div>
                <div class="tip-desc">联系客服人员，获取专业帮助</div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 修改昵称对话框 -->
    <el-dialog
        v-model="showNicknameDialog"
        width="420px"
        :close-on-click-modal="false"
        :show-close="false"
        class="nickname-dialog"
    >
      <!-- 自定义头部 -->
      <template #header>
        <div class="dialog-custom-header">
          <span class="dialog-title">修改昵称</span>
          <el-icon class="dialog-close" @click="closeNicknameDialog">
            <Close />
          </el-icon>
        </div>
      </template>

      <!-- 主体内容 -->
      <div class="nickname-dialog-body">
        <el-form @submit.prevent="handleNicknameUpdate">
          <el-form-item label="新昵称">
            <el-input
                v-model="nicknameInput"
                placeholder="请输入新昵称"
                maxlength="7"
                show-word-limit
                clearable
                @keyup.enter="handleNicknameUpdate"
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- 底部按钮 -->
      <template #footer>
        <el-button @click="closeNicknameDialog">取消</el-button>
        <el-button type="primary" @click="handleNicknameUpdate">确认</el-button>
      </template>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog
        v-model="showPasswordDialog"
        title=""
        width="480px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        class="password-dialog"
    >

      <template #header>
        <div class="dialog-custom-header">
          <span class="dialog-title">修改密码</span>
          <el-icon class="dialog-close" @click="closePasswordDialog">
            <Close />
          </el-icon>
        </div>
      </template>

      <PasswordUpdateDialog
          v-if="showPasswordDialog"
          @success="handlePasswordChangeSuccess"
          @cancel="closePasswordDialog"
      />
    </el-dialog>

    <!-- 头像上传对话框 -->
    <el-dialog
        v-model="showAvatarDialog"
        width="480px"
        :close-on-click-modal="false"
        :show-close="false"
        class="avatar-dialog"
    >

      <template #header>
        <div class="dialog-custom-header">
          <span class="dialog-title">更换头像</span>
          <el-icon class="dialog-close" @click="closeAvatarDialog">
            <Close />
          </el-icon>
        </div>
      </template>

      <div class="avatar-upload-form">
        <div class="upload-area">
          <!-- 预览区域 -->
          <div v-if="avatarPreviewUrl" class="preview-area">
            <img :src="avatarPreviewUrl" alt="头像预览" class="preview-image" />
            <div class="preview-text">预览</div>
          </div>

          <!-- 上传区域 -->
          <div class="upload-section">
            <el-upload
                drag
                :auto-upload="false"
                :show-file-list="false"
                :on-change="handleAvatarChange"
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                class="avatar-uploader"
            >
              <div class="upload-content">
                <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                <div class="el-upload__text">
                  将文件拖到此处，或<em>点击选择</em>
                </div>
                <div class="el-upload__tip">
                  支持 JPG、PNG、GIF、BMP、WebP 格式，大小不超过10MB
                </div>
              </div>
            </el-upload>
          </div>
        </div>

        <div class="dialog-actions">
          <el-button @click="closeAvatarDialog" :disabled="avatarUploading">取消</el-button>
          <el-button
              type="primary"
              @click="uploadAvatar"
              :loading="avatarUploading"
              :disabled="!avatarFile"
          >
            {{ avatarUploading ? '上传中...' : '确认上传' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 修改手机对话框 -->
    <el-dialog
        v-model="showChangePhoneDialog"
        title=""
        width="480px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        class="change-phone-dialog"
    >

      <template #header>
        <div class="dialog-custom-header">
      <span class="dialog-title">
        更换手机
      </span>
          <el-icon class="dialog-close" @click="closeChangePhoneDialog">
            <Close />
          </el-icon>
        </div>
      </template>

      <div class="change-phone-form">
        <!-- 步骤指示器 -->
        <div class="stepper">
          <div class="stepper-item" :class="{ active: changePhoneStep >= 1, done: changePhoneStep > 1 }">
            <div class="stepper-circle">1</div>
            <span class="stepper-label">验证当前手机</span>
          </div>
          <div class="stepper-line" :class="{ active: changePhoneStep > 1 }"></div>
          <div class="stepper-item" :class="{ active: changePhoneStep >= 2 }">
            <div class="stepper-circle">2</div>
            <span class="stepper-label">绑定新手机</span>
          </div>
        </div>

        <!-- 第一步：验证旧手机 -->
        <div v-if="changePhoneStep === 1" class="step-content">
          <div class="current-phone-info">
            <el-icon class="info-icon"><Iphone /></el-icon>
            <div>
              <div class="info-label-small">当前绑定的手机号</div>
              <div class="info-phone">{{ maskedCurrentPhone }}</div>
            </div>
          </div>
          <el-form>
            <el-form-item label="验证码">
              <div class="verify-code-input">
                <el-input
                    v-model="oldPhoneVerifyCode"
                    placeholder="请输入收到的验证码"
                    maxlength="6"
                />
                <el-button
                    type="primary"
                    :disabled="oldPhoneCodeCooldown > 0"
                    @click="sendOldPhoneCode"
                    class="code-btn"
                >
                  {{ oldPhoneCodeCooldown > 0 ? `${oldPhoneCodeCooldown}s` : '获取验证码' }}
                </el-button>
              </div>
            </el-form-item>
          </el-form>
          <div class="dialog-actions">
            <el-button @click="closeChangePhoneDialog">取消</el-button>
            <el-button type="primary" @click="verifyOldPhone">下一步</el-button>
          </div>
        </div>

        <!-- 第二步：绑定新手机 -->
        <div v-if="changePhoneStep === 2" class="step-content">
          <el-form>
            <el-form-item label="新手机号">
              <el-input
                  v-model="newPhone"
                  placeholder="请输入新手机号"
                  maxlength="11"
              />
            </el-form-item>
            <el-form-item label="验证码">
              <div class="verify-code-input">
                <el-input
                    v-model="newPhoneVerifyCode"
                    placeholder="请输入验证码"
                    maxlength="6"
                />
                <el-button
                    type="primary"
                    :disabled="newPhoneCodeCooldown > 0 || !newPhone"
                    @click="sendNewPhoneCode"
                    class="code-btn"
                >
                  {{ newPhoneCodeCooldown > 0 ? `${newPhoneCodeCooldown}s` : '获取验证码' }}
                </el-button>
              </div>
            </el-form-item>
          </el-form>
          <div class="dialog-actions">
            <el-button @click="changePhoneStep = 1">上一步</el-button>
            <el-button type="primary" @click="confirmChangePhone">确认修改</el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import request from '@/utils/request'
import {ElMessage} from 'element-plus'
import {useRouter} from 'vue-router'
import { Close } from '@element-plus/icons-vue'
import PasswordUpdateDialog from '@/components/PasswordUpdateDialog.vue'
import {eventBus} from '@/utils/eventBus'
import { authState } from '@/stores/authState'

const router = useRouter()
const profile = ref({
  avatar: '',
  nickname: '',
  username: '',
  userType: '',
  userLevel: '',
  email: '-',
  phone: '-',
  hasPassword: false,
  hasWechatBind: false,
  loginMethods: []
})

const loading = ref(false)
const showPasswordDialog = ref(false)
const showNicknameDialog = ref(false)
const newNickname = ref('')
const nicknameInput = ref('')   // 弹窗中的昵称临时输入

// 修改手机号相关
const showChangePhoneDialog = ref(false)
const changePhoneStep = ref(1)
const oldPhoneVerifyCode = ref('')
const newPhone = ref('')
const newPhoneVerifyCode = ref('')
const oldPhoneCodeCooldown = ref(0)
const newPhoneCodeCooldown = ref(0)
const maskedCurrentPhone = ref('')

// 头像上传相关
const showAvatarDialog = ref(false)
const avatarFile = ref(null)
const avatarUploading = ref(false)
const avatarPreviewUrl = ref('')

// 默认头像
const defaultAvatar = ref('https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png')

// 加载用户信息
const loadUserProfile = async () => {
  loading.value = true
  try {
    const response = await request.get('/user/profile')
    if (response.code === '200') {
      profile.value = response.data
      newNickname.value = profile.value.nickname
    } else {
      ElMessage.error(response.message || '获取用户信息失败')
    }
  } catch (error) {
    console.error('获取用户信息失败:', error)
  } finally {
    loading.value = false
  }
}

// 处理头像点击
const handleAvatarClick = () => {
  showAvatarDialog.value = true
}

// 处理文件选择
const handleFileChange = (file) => {
  avatarFile.value = file
  // 创建预览URL
  const reader = new FileReader()
  reader.onload = (e) => {
    avatarPreviewUrl.value = e.target.result
  }
  reader.readAsDataURL(file)
}

// 头像上传变化处理
const handleAvatarChange = (uploadFile) => {
  const file = uploadFile.raw
  if (!file) return

  // 验证文件类型
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp']
  const isImage = validTypes.includes(file.type)
  const isLt10M = file.size / 1024 / 1024 < 10

  if (!isImage) {
    ElMessage.error('请选择图片文件（JPEG、PNG、GIF、BMP、WebP）')
    return
  }
  if (!isLt10M) {
    ElMessage.error('图片大小不能超过10MB')
    return
  }

  handleFileChange(file)
}

// 上传头像
const uploadAvatar = async () => {
  if (!avatarFile.value) {
    ElMessage.error('请选择要上传的图片')
    return
  }

  avatarUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', avatarFile.value)

    const response = await request.post('/user/update-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (response.code === '200') {
      // ElMessage.success('头像更新成功')
      profile.value.avatar = response.data
      showAvatarDialog.value = false
      avatarFile.value = null
      avatarPreviewUrl.value = ''

      // 发送头像更新事件
      eventBus.emit('user-profile-updated', {
        type: 'avatar',
        data: response.data
      })

      // 重新加载用户信息以确保数据一致
      await loadUserProfile()
    } else {
      ElMessage.error(response.message || '头像上传失败')
    }
  } catch (error) {
    console.error('上传头像失败:', error)
    ElMessage.error('头像上传失败，请稍后重试')
  } finally {
    avatarUploading.value = false
  }
}

// 关闭头像对话框
const closeAvatarDialog = () => {
  showAvatarDialog.value = false
  avatarFile.value = null
  avatarPreviewUrl.value = ''
}

// 打开修改手机对话框
const openChangePhoneDialog = () => {
  showChangePhoneDialog.value = true
  changePhoneStep.value = 1
  oldPhoneVerifyCode.value = ''
  newPhone.value = ''
  newPhoneVerifyCode.value = ''

  // 显示脱敏的当前手机号
  maskedCurrentPhone.value = profile.value.phone || '-'
}

// 关闭修改手机对话框
const closeChangePhoneDialog = () => {
  showChangePhoneDialog.value = false
  changePhoneStep.value = 1
  oldPhoneVerifyCode.value = ''
  newPhone.value = ''
  newPhoneVerifyCode.value = ''
  oldPhoneCodeCooldown.value = 0
  newPhoneCodeCooldown.value = 0
}

// 发送旧手机验证码
const sendOldPhoneCode = async () => {
  try {
    const response = await request.post('/user/send-change-phone-code', {
      phoneType: 'old'
      // 不再传递手机号，后端会通过userId自动获取
    })

    if (response.code === '200') {
      ElMessage.success('验证码发送成功')
      startOldPhoneCooldown()
    } else {
      ElMessage.error(response.message || '验证码发送失败')
    }
  } catch (error) {
    console.error('发送验证码失败:', error)
    ElMessage.error('验证码发送失败')
  }
}

// 发送新手机验证码
const sendNewPhoneCode = async () => {
  if (!newPhone.value.trim()) {
    ElMessage.error('请输入新手机号')
    return
  }

  if (!newPhone.value.match(/^1[3-9]\d{9}$/)) {
    ElMessage.error('手机号格式不正确')
    return
  }

  try {
    const response = await request.post('/user/send-change-phone-code', {
      phoneType: 'new',
      newPhone: newPhone.value.trim() // 只传递新手机号
    })

    if (response.code === '200') {
      ElMessage.success('验证码发送成功')
      startNewPhoneCooldown()
    } else {
      ElMessage.error(response.message || '验证码发送失败')
    }
  } catch (error) {
    console.error('发送验证码失败:', error)
    ElMessage.error('验证码发送失败')
  }
}

// 验证旧手机
const verifyOldPhone = async () => {
  if (!oldPhoneVerifyCode.value.trim()) {
    ElMessage.error('请输入验证码')
    return
  }

  try {
    const response = await request.post('/user/verify-old-phone', {
      verifyCode: oldPhoneVerifyCode.value.trim()
    })

    if (response.code === '200') {
      ElMessage.success('验证成功')
      changePhoneStep.value = 2
    } else {
      ElMessage.error(response.message || '验证失败')
    }
  } catch (error) {
    console.error('验证旧手机失败:', error)
    ElMessage.error('验证失败')
  }
}

// 确认修改手机
const confirmChangePhone = async () => {
  if (!newPhone.value.trim()) {
    ElMessage.error('请输入新手机号')
    return
  }

  if (!newPhoneVerifyCode.value.trim()) {
    ElMessage.error('请输入验证码')
    return
  }

  try {
    const response = await request.post('/user/change-phone', {
      newPhone: newPhone.value.trim(),
      newPhoneVerifyCode: newPhoneVerifyCode.value.trim()
    })

    if (response.code === '200') {
      ElMessage.success('手机号修改成功')
      closeChangePhoneDialog()
      // 重新加载用户信息
      await loadUserProfile()
    } else {
      ElMessage.error(response.message || '修改失败')
    }
  } catch (error) {
    console.error('修改手机号失败:', error)
    ElMessage.error('修改失败')
  }
}

// 倒计时计时器
const startOldPhoneCooldown = () => {
  oldPhoneCodeCooldown.value = 60
  const timer = setInterval(() => {
    oldPhoneCodeCooldown.value--
    if (oldPhoneCodeCooldown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

const startNewPhoneCooldown = () => {
  newPhoneCodeCooldown.value = 60
  const timer = setInterval(() => {
    newPhoneCodeCooldown.value--
    if (newPhoneCodeCooldown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}


const handleAction = (action, method) => {
  switch (action) {
    case 'set':
      ElMessage.info('功能开发中...')
      break
    case 'modify':
      // 修改手机号
      openChangePhoneDialog()
      break
    case 'unbind':
      ElMessage.info('功能开发中...')
      break
    case 'bind':
      ElMessage.info('功能开发中...')
      break
    case 'changePassword':
      showPasswordDialog.value = true
      break
    case 'editNickname':
      openNicknameDialog()
      break
  }
}

const getAvatarUrl = () => {
  return profile.value.avatar || defaultAvatar.value
}

const closePasswordDialog = () => {
  showPasswordDialog.value = false
}

// 关闭修改昵称弹窗
const closeNicknameDialog = () => {
  showNicknameDialog.value = false
  nicknameInput.value = ''   // 清空临时数据
}

// 打开修改昵称弹窗
const openNicknameDialog = () => {
  nicknameInput.value = profile.value.nickname || ''  // 填入当前昵称
  showNicknameDialog.value = true
}

const handlePasswordChangeSuccess = () => {
  showPasswordDialog.value = false
  ElMessage.success('密码修改成功，请重新登录')

  setTimeout(() => {
    localStorage.removeItem('auth_token');
    authState.clear();
    router.push('/login')
  }, 1500)
}

const handleNicknameUpdate = async () => {
  const newName = nicknameInput.value.trim()
  if (!newName) {
    ElMessage.error('昵称不能为空')
    return
  }

  if (newName.length > 7) {
    ElMessage.error('昵称不能超过7个字符')
    return
  }

  if (newName === profile.value.nickname) {
    ElMessage.info('昵称未发生变化')
    closeNicknameDialog()
    return
  }

  try {
    const response = await request.put('/user/update-nickname', {
      nickname: newName
    })

    if (response.code === '200') {
      profile.value.nickname = newName
      closeNicknameDialog()

      // 发送昵称更新事件
      eventBus.emit('user-profile-updated', {
        type: 'nickname',
        data: newName
      })
    } else {
      ElMessage.error(response.message || '昵称修改失败')
    }
  } catch (error) {
    console.error('修改昵称失败:', error)
    ElMessage.error('昵称修改失败，请稍后重试')
  }
}

onMounted(() => {
  loadUserProfile()
})
</script>

<style scoped>
/* ========== 根容器 ========== */
.user-profile {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding: 10px 6px;
  margin: 0 auto;
  width: 92%;
  height: 100%;
  min-height: 700px;
  box-sizing: border-box;
  border: none;
  background: transparent;
}

.profile-content {
  display: flex;
  flex-direction: column;
}

/* ========== 主卡片 ========== */
.main-card {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden;
}

.main-card:hover {

}

:deep(.el-card__body) {
  padding: 0;
  background: transparent;
}

/* ========== 区域通用 ========== */
.section {
  padding: 22px 28px;
}

.section-header {
  margin-bottom: 18px;
}

.section-title {
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  position: relative;
  padding-left: 14px;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(180deg, #2f5bff, #f59e0b);
}

.section-title-with-subtitle {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.section-subtitle {
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 400;
}

/* 分隔线 */
.section-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.9), transparent);
  margin: 0;
}

/* ========== 基本信息区域 ========== */
.basic-info-section {
  background:
      radial-gradient(ellipse 300px 200px at 0% 0%, rgba(47, 91, 255, 0.03), transparent 60%),
      #ffffff;
}

.basic-info-content {
  display: flex;
  gap: 28px;
  align-items: flex-start;
}

.avatar-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-top: 4px;
  width: 110px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.avatar-area:hover {
  opacity: 0.85;
}

.user-avatar {
  border: 3px solid transparent;
  background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(135deg, #2f5bff, #f59e0b) border-box;
  box-shadow: 0 8px 24px rgba(47, 91, 255, 0.18);
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s ease;
}

.avatar-area:hover .user-avatar {
  transform: scale(1.06);
  box-shadow: 0 12px 32px rgba(245, 158, 11, 0.24);
}

.avatar-hint {
  margin-top: 4px;
  text-align: center;
}

.hint-text {
  color: #2f5bff;
  font-size: 0.78rem;
  cursor: pointer;
  font-weight: 500;
}

.info-area {
  flex: 1;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.info-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  padding: 11px 0;
  border-bottom: 1px solid rgba(241, 245, 249, 0.9);
}

.info-row:last-child {
  border-bottom: none;
}

.info-item {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.info-label {
  width: 80px;
  color: #64748b;
  font-size: 0.86rem;
  margin-right: 16px;
  flex-shrink: 0;
  font-weight: 500;
}

.info-value {
  color: #1e293b;
  font-size: 0.9rem;
  flex: 1;
  font-weight: 500;
}

.info-with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

/* 修改昵称对话框 */
.nickname-dialog {
  :deep(.el-dialog) {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.16);
  }

  :deep(.el-dialog__header) {
    padding: 20px 24px 12px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.9);
  }

  :deep(.el-dialog__title) {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
  }

  :deep(.el-dialog__body) {
    padding: 24px 24px 8px;
  }

  :deep(.el-dialog__footer) {
    padding: 12px 24px 20px;
    border-top: none;
  }

  .nickname-dialog-body {
    .el-form-item {
      margin-bottom: 0;
    }

    .el-input__wrapper {
      border-radius: 10px;
    }
  }
}

/* ========== 昵称样式 ========== */

@keyframes inlineEditIn {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}

.nickname-input :deep(.el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 0 0 1.5px #2f5bff inset, 0 0 0 4px rgba(47, 91, 255, 0.1);
  height: 30px;
}

/* 用户等级标签 */
.user-level-tag {
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 600;
  padding: 3px 10px;
  background: linear-gradient(135deg, rgba(47, 91, 255, 0.1), rgba(245, 158, 11, 0.1));
  border: 1px solid rgba(47, 91, 255, 0.2);
  color: #1f43cc;
}

.info-value .user-level-tag {
  margin: 0;
}

/* 编辑按钮 */
.edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0 !important;
  margin-left: 8px;
  border-radius: 50% !important;
  background: rgba(47, 91, 255, 0.08) !important;   /* 极浅蓝背景 */
  border: 1px solid rgba(47, 91, 255, 0.15) !important; /* 浅蓝边框 */
  color: #2f5bff !important;                        /* 主蓝色图标 */
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
}

.edit-btn .el-icon {
  font-size: 15px;
  transition: transform 0.3s ease;
}

/* 悬浮状态：背景加深，边框变实，轻微放大 + 阴影 */
.edit-btn:hover {
  background: rgba(47, 91, 255, 0.18) !important;
  border-color: #2f5bff !important;
  transform: scale(1.06);
  box-shadow: 0 4px 14px rgba(47, 91, 255, 0.20);
}

.edit-btn:hover .el-icon {
  transform: scale(1.1) rotate(-6deg);
  color: #1f43cc;  /* 略深蓝 */
}

/* 点击反馈：微缩放 */
.edit-btn:active {
  transform: scale(0.92);
}

/* ---------- 修改密码按钮样式 ---------- */
.pwd-modify-btn {
  background-color: #eef3ff !important;        /* 浅蓝背景 */
  border: 1px solid #8aa0ff !important;        /* 浅蓝边框 */
  color: #142c84 !important;                  /* 深蓝色文字 */
  border-radius: 6px;
  font-weight: 500;
  padding: 4px 14px;
  height: 22px;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.pwd-modify-btn:hover {
  background-color: #dce6ff !important;        /* 悬浮加重背景 */
  border-color: #2f5bff !important;            /* 边框变深蓝 */
  color: #142c84 !important;                  /* 文字更深 */
  box-shadow: 0 2px 10px rgba(47, 91, 255, 0.15);
  transform: translateY(-1px);
}

.pwd-modify-btn:active {
  transform: scale(0.96);
}

.edit-btn:hover .el-icon {
  transform: scale(1.15) rotate(-8deg);
  color: #f59e0b;
}

/* 自定义弹窗头部 */
.dialog-custom-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
}

.dialog-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
}

.dialog-close {
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;

  svg {
    stroke-width: 10;      /* 加粗描边 */
    stroke: currentColor; /* 继承颜色 */
  }
}

.dialog-close:hover {
  color: #dc2626;
  background: rgba(186, 172, 172, 0.08);
}

/* ========== 登录方式区域 ========== */
.login-methods-section {
  margin-top: 20px;
  background: transparent;
}

.methods-grid-container {
  margin-left: 138px;
}

.methods-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}



.method-grid-item {
  padding: 18px 20px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.6);
  border: 1px solid rgba(241, 245, 249, 0.8);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.method-grid-item:hover {
  background: rgba(47, 91, 255, 0.04);
  border-color: rgba(47, 91, 255, 0.18);
  transform: translateY(-2px);
}

.empty-item {
  border: none;
  background: transparent;
}

.empty-item:hover {
  background: transparent;
  border-color: transparent;
  transform: none;
}

.method-content-single-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.method-info-single-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.method-type {
  color: #1e293b;
  font-size: 0.86rem;
  font-weight: 600;
  min-width: 60px;
}

.method-value {
  color: #94a3b8;
  font-size: 0.78rem;
}

.method-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.method-status {
  font-size: 0.74rem;
  font-weight: 600;
  min-width: 44px;
  text-align: center;
  padding: 3px 10px;
  border-radius: 999px;
}

.status-bound {
  color: #16a34a;
  background: rgba(22, 163, 74, 0.1);
}

.status-unbound {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.1);
}

/* ========== 底部安全提示区域 ========== */
.description-section {
  margin-top: 20px;
  position: relative;
  background:
      radial-gradient(ellipse 400px 100px at 50% 100%, rgba(47, 91, 255, 0.04), transparent 70%),
      linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  padding: 18px 28px;
}

/* 顶部渐变分割线：中间实、两端虚 */
.description-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
      to right,
      transparent 0%,
      rgba(226, 232, 240, 0.9) 20%,
      rgba(226, 232, 240, 0.9) 80%,
      transparent 100%
  );
}

.security-tips {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.security-tip-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  padding: 0 16px;
}

.security-tip-item:first-child {
  padding-left: 0;
}

.security-tip-item:last-child {
  padding-right: 0;
}

.security-tip-divider {
  width: 1px;
  background: linear-gradient(180deg, transparent, rgba(226, 232, 240, 0.9), transparent);
  flex-shrink: 0;
}

.tip-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.security-tip-item:hover .tip-icon {
  transform: scale(1.08);
}

.tip-icon--blue {
  background: rgba(47, 91, 255, 0.1);
  color: #2f5bff;
}

.tip-icon--orange {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.tip-icon--green {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}

.tip-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tip-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.tip-desc {
  font-size: 0.74rem;
  color: #94a3b8;
  line-height: 1.4;
}

/* ========== 弹窗通用样式 ========== */
.password-dialog,
.avatar-dialog,
.change-phone-dialog {
  :deep(.el-dialog) {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.16), 0 8px 24px rgba(47, 91, 255, 0.08);
    border: 1px solid rgba(226, 232, 240, 0.8);
  }

  :deep(.el-dialog__header) {
    padding: 22px 24px 16px;
    margin: 0;
    border-bottom: 1px solid rgba(241, 245, 249, 0.9);
    background:
        radial-gradient(ellipse 220px 70px at 10% 0%, rgba(47, 91, 255, 0.08), transparent 70%),
        radial-gradient(ellipse 180px 60px at 90% 10%, rgba(245, 158, 11, 0.06), transparent 70%),
        #ffffff;
  }

  :deep(.el-dialog__title) {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: 0.01em;
  }

  :deep(.el-dialog__headerbtn) {
    top: 18px;
    right: 18px;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: rgba(241, 245, 249, 0.6);
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 5;
  }

  :deep(.el-dialog__headerbtn:hover) {
    background: rgba(220, 38, 38, 0.1);
  }

  :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: #64748b;
    font-size: 16px;
  }

  :deep(.el-dialog__headerbtn:hover .el-dialog__close) {
    color: #dc2626;
  }

  :deep(.el-dialog__body) {
    padding: 24px;
  }
}

/* ========== 头像上传对话框 ========== */
.avatar-upload-form {
  .upload-area {
    display: flex;
    gap: 24px;
    margin-bottom: 20px;
  }

  .preview-area {
    flex: 0 0 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }

  .preview-image {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid transparent;
    background:
        linear-gradient(#fff, #fff) padding-box,
        linear-gradient(135deg, #2f5bff, #f59e0b) border-box;
    box-shadow: 0 8px 24px rgba(47, 91, 255, 0.18);
  }

  .upload-section {
    flex: 1;
  }

  .avatar-uploader {
    :deep(.el-upload) {
      width: 100%;
    }

    :deep(.el-upload-dragger) {
      width: 100%;
      height: 180px;
      border: 2px dashed #cbd5e1;
      border-radius: 14px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      background: rgba(248, 250, 252, 0.5);

      &:hover {
        border-color: #2f5bff;
        background: rgba(47, 91, 255, 0.04);
      }
    }

    .upload-content {
      padding: 24px;
      text-align: center;
    }

    .el-icon--upload {
      font-size: 44px;
      color: #2f5bff;
      margin-bottom: 14px;
    }

    .el-upload__text {
      color: #475569;
      font-size: 0.88rem;
      margin-bottom: 8px;

      em {
        color: #2f5bff;
        font-style: normal;
        font-weight: 600;
      }
    }

    .el-upload__tip {
      color: #94a3b8;
      font-size: 0.76rem;
      line-height: 1.6;
    }
  }

  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
  }
}

.preview-text {
  position: absolute;
  bottom: -26px;
  left: 50%;
  transform: translateX(-50%);
  color: #94a3b8;
  font-size: 0.76rem;
  white-space: nowrap;
}

/* ========== 修改手机对话框 (步骤指示器) ========== */
.change-phone-form {
  .stepper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 28px;
    padding: 0 20px;
  }

  .stepper-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .stepper-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.86rem;
    font-weight: 700;
    background: rgba(241, 245, 249, 0.8);
    color: #94a3b8;
    border: 2px solid rgba(226, 232, 240, 0.9);
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .stepper-item.active .stepper-circle {
    background: linear-gradient(135deg, #2f5bff, #1f43cc);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(47, 91, 255, 0.3);
  }

  .stepper-item.done .stepper-circle {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #fff;
    border-color: transparent;
  }

  .stepper-label {
    font-size: 0.78rem;
    color: #94a3b8;
    font-weight: 500;
    white-space: nowrap;
    transition: color 0.3s ease;
  }

  .stepper-item.active .stepper-label,
  .stepper-item.done .stepper-label {
    color: #1e293b;
    font-weight: 600;
  }

  .stepper-line {
    flex: 1;
    height: 2px;
    background: rgba(226, 232, 240, 0.9);
    margin: 0 12px;
    margin-bottom: 24px;
    border-radius: 1px;
    transition: background 0.4s ease;
    max-width: 80px;
  }

  .stepper-line.active {
    background: linear-gradient(90deg, #2f5bff, #22c55e);
  }

  .step-content {
    .current-phone-info {
      display: flex;
      align-items: center;
      gap: 14px;
      background: linear-gradient(135deg, rgba(47, 91, 255, 0.06), rgba(245, 158, 11, 0.04));
      border: 1px solid rgba(47, 91, 255, 0.12);
      border-radius: 14px;
      padding: 16px 18px;
      margin-bottom: 20px;
    }

    .info-icon {
      font-size: 22px;
      color: #2f5bff;
      flex-shrink: 0;
    }

    .info-label-small {
      font-size: 0.76rem;
      color: #94a3b8;
      margin-bottom: 2px;
    }

    .info-phone {
      font-size: 1rem;
      font-weight: 700;
      color: #1e293b;
      letter-spacing: 0.04em;
    }

    .verify-code-input {
      display: flex;
      gap: 10px;
      width: 100%;

      .el-input {
        flex: 1;
      }
    }

    .code-btn {
      border-radius: 10px;
      font-weight: 600;
      min-width: 110px;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 24px;
    }
  }
}

/* ========== 按钮通用美化 ========== */
:deep(.el-button--primary) {
  background: linear-gradient(135deg, #2f5bff, #1f43cc);
  border: none;
  border-radius: 10px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(47, 91, 255, 0.25);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

:deep(.el-button--primary:hover) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(47, 91, 255, 0.35);
}

:deep(.el-button--default) {
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  color: #475569;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

:deep(.el-button--default:hover) {
  border-color: rgba(47, 91, 255, 0.3);
  color: #1f43cc;
  background: rgba(47, 91, 255, 0.04);
}

/* ========== 输入框美化 ========== */
:deep(.el-input__wrapper) {
  border-radius: 10px;
  box-shadow: 0 0 0 1px rgba(226, 232, 240, 0.9) inset;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #cbd5e1 inset;
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #2f5bff inset, 0 0 0 4px rgba(47, 91, 255, 0.12) !important;
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .user-profile {
    padding: 8px 12px;
  }

  .section {
    padding: 20px 18px;
  }

  .basic-info-content {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .avatar-area {
    margin-top: 0;
  }

  .info-area {
    width: 100%;
  }

  .info-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .methods-grid-container {
    margin-left: 0;
  }

  .methods-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .security-tips {
    flex-direction: column;
    gap: 12px;
  }

  .security-tip-item {
    padding: 0;
  }

  .security-tip-divider {
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.9), transparent);
  }

  .avatar-upload-form .upload-area {
    flex-direction: column;
    align-items: center;
  }

  .change-phone-form .verify-code-input {
    flex-direction: column;
  }

  .change-phone-form .stepper-line {
    max-width: 40px;
    margin: 0 6px;
  }
}
</style>
